import React, { useMemo } from 'react';
import { find, trimEnd } from 'lodash';

import { PanelHeader, ChartSelector } from 'js/components';
import { AssessmentReviewBreakoutRoutes } from 'js/components/Breakouts';
import { useAssessmentBreakoutSelection } from 'js/hooks/useAssessmentBreakoutSelection';
import { useChartSearchParam } from 'js/hooks/useChartSearchParam';

import ReviewHeader from './ReviewHeader';
import AssessmentHistoryChart, {
  Glyph as ScoreGlyph,
} from './AssessmentHistoryChart';
import AssessmentHistoryStackedBarChart, {
  Glyph as BarGlyph,
} from './AssessmentHistoryStackedBarChart';
import AssessmentHistoryBoxPlot, {
  Glyph as BoxPlotGlyph,
} from './AssessmentHistoryBoxPlot';
import AssessmentHistoryRadialPieChart, {
  Glyph as RadialPieGlyph,
} from './AssessmentHistoryRadialPieChart';
import { Glyph as RadarGlyph } from 'js/components/Charts/SeriesRadarChart';
import CohortSeriesRadarChart from './CohortSeriesRadarChart';

import { Route, Switch } from 'react-router';
import AssessmentReviewDetailsResults from './AssessmentReviewDetailResults';
import AssessmentReviewDetailsInvitees from './AssessmentReviewDetailInvitees';
import AssessmentReviewDetailOverview from './AssessmentReviewDetailOverview';
import { Nav, NavItem, NavLink } from 'reactstrap';
import { Link } from 'react-router-dom';
import { getSummaryMetrics } from 'js/utils/question-set';

const AssessmentReviewDetail = ({
  assessments,
  team,
  name,
  assessmentKey,
  categoryLookups,
  categoryColorMap,
  match,
  location,
  onSendInvites,
  sendingInvites,
  onSendReminders,
  assessmentInvitesQueryResult,
  onUpdateBreakouts,
  savingBreakouts,
}) => {
  const {
    params: { assessmentId },
  } = match;
  const currentView =
    location.pathname.slice(match.url.length)?.replaceAll('/', '') ||
    'overview';

  const assessment = useMemo(
    () => find(assessments, (a) => a.id === assessmentId),
    [assessments, assessmentId]
  );

  const {
    selectedBreakoutId,
    handleBreakoutChange,
    filter,
    availableBreakouts,
    participantCohortMap,
    legendFields,
    activeBreakoutName,
    activeCohort,
    pinnedCohort,
    cohortProps,
    handlePinCohort,
    handleClearPinnedCohort,
    handlePeekCohort,
    handleClearPeekedCohort,
  } = useAssessmentBreakoutSelection(assessment);

  const dataMap = useMemo(
    () =>
      !assessment
        ? null
        : getSummaryMetrics([assessment], {
            filter,
            participantCohortsMap: participantCohortMap,
          }),
    [assessment, filter, participantCohortMap]
  );

  const [chartKey, setChartKey] = useChartSearchParam();
  const showCharts = currentView === 'overview' || currentView === 'results';
  const onBreakoutsTab =
    currentView === 'breakouts' || currentView.startsWith('breakouts');
  const breakoutsBase = `${trimEnd(match.url, '/')}/breakouts`;

  return (
    <div className="survey-assessment review">
      <PanelHeader size="flex">
        <ReviewHeader
          glow={assessment === undefined}
          name={name}
          date={assessment ? assessment.inserted_at : undefined}
          team={team}
          dataMap={dataMap}
          availableBreakouts={availableBreakouts}
          selectedBreakoutId={selectedBreakoutId}
          onBreakoutChange={handleBreakoutChange}
          categoryColorMap={categoryColorMap}
          assessment={assessment}
          invites={assessmentInvitesQueryResult}
          assessmentKey={assessmentKey}
          categoryLookups={categoryLookups}
          showParticipationStats
          showCharts={showCharts}
        >
          <label>Charts</label>

          {assessment === undefined ? null : (
            <ChartSelector
              activeKey={chartKey}
              onChange={setChartKey}
              charts={[
                {
                  label: RadialPieGlyph,
                  component: AssessmentHistoryRadialPieChart,
                  buttonProps: { 'data-chart-type': 'radial-pie' },
                },
                {
                  label: RadarGlyph,
                  component: CohortSeriesRadarChart,
                  buttonProps: { 'data-chart-type': 'radar' },
                },
                {
                  label: ScoreGlyph,
                  component: AssessmentHistoryChart,
                  buttonProps: { 'data-chart-type': 'assessment-history' },
                },
                {
                  label: BarGlyph,
                  component: AssessmentHistoryStackedBarChart,
                  buttonProps: { 'data-chart-type': 'stacked-bar' },
                },
                {
                  label: BoxPlotGlyph,
                  component: AssessmentHistoryBoxPlot,
                  buttonProps: { 'data-chart-type': 'box-plot' },
                },
              ]}
              assessmentId={assessmentId}
              assessment={assessment}
              dataMap={dataMap}
              categoryColorMap={categoryColorMap}
              categoryLookups={categoryLookups}
              legendFields={legendFields}
              participantCohortMap={participantCohortMap}
              activeCohort={activeCohort}
              pinnedCohort={pinnedCohort}
              breakoutName={activeBreakoutName}
              onPinCohort={handlePinCohort}
              onClearPinnedCohort={handleClearPinnedCohort}
              onPeekCohort={handlePeekCohort}
              onClearPeekedCohort={handleClearPeekedCohort}
              questionSetHref={(questionSetKey) => `#${questionSetKey}`}
              questionHref={(questionSetKey, questionKey) =>
                `#${questionSetKey}_${questionKey}`
              }
            />
          )}
        </ReviewHeader>
        <div className="container-fluid">
          <Nav className="tab-nav nav-underline">
            <NavItem>
              <NavLink
                tag={Link}
                active={currentView === 'overview'}
                data-view="overview"
                to={`${trimEnd(match.url, '/')}${location.search}`}
              >
                Overview
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={Link}
                active={currentView === 'results'}
                data-view="results"
                to={`${trimEnd(match.url, '/')}/results${location.search}`}
              >
                Results
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={Link}
                active={onBreakoutsTab}
                data-view="breakouts"
                to={`${breakoutsBase}${location.search}`}
              >
                Breakouts
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={Link}
                active={currentView === 'invitees'}
                data-view="invitees"
                to={`${trimEnd(match.url, '/')}/invitees${location.search}`}
              >
                Invitations
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      </PanelHeader>
      <Switch>
        <Route
          path={`${match.path}/overview`}
          render={() => (
            <AssessmentReviewDetailOverview
              assessment={assessment}
              filter={filter}
              dataMap={dataMap}
              {...cohortProps}
            />
          )}
        />
        <Route
          path={`${match.path}/results`}
          render={() => (
            <AssessmentReviewDetailsResults
              assessment={assessment}
              filter={filter}
              {...cohortProps}
            />
          )}
        />
        <AssessmentReviewBreakoutRoutes
          path={`${match.path}/breakouts`}
          assessment={assessment}
          onSave={onUpdateBreakouts}
          saving={savingBreakouts}
          categoryColorMap={categoryColorMap}
          listPath={breakoutsBase}
        />
        <Route
          path={`${match.path}/invitees`}
          render={() => (
            <AssessmentReviewDetailsInvitees
              onSendInvites={onSendInvites}
              sendingInvites={sendingInvites}
              onSendReminders={onSendReminders}
              queryResult={assessmentInvitesQueryResult}
              assessment={assessment}
              team={team}
            />
          )}
        />
        <Route
          path={`${match.path}`}
          render={() => (
            <AssessmentReviewDetailOverview
              assessment={assessment}
              filter={filter}
              dataMap={dataMap}
              {...cohortProps}
            />
          )}
        />
      </Switch>
    </div>
  );
};

export default AssessmentReviewDetail;
